import { IconLesson, styled, TextField, useForm, useWatch } from '@lessonup/ui-components';
import { isEmpty, isString } from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { TRANSLATION_NAMESPACE } from '../constantsV2';
import { Footer } from './Footer';
import { LessonModalContent, LessonModalMainWrapper } from './LayoutComponents';

interface EditorSelectionProps {
  editorType: 'v1' | 'v2';
  back: () => void;
  createManualLesson: (name: string, version: 'v1' | 'v2') => void;
}

export const EditorSelection: React.FC<EditorSelectionProps> = ({ editorType, createManualLesson, back }) => {
  const {
    control,
    register,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<{ lessonTitle: string }>();

  const { t } = useTranslation(TRANSLATION_NAMESPACE, { keyPrefix: 'editorSelection' });

  const lessonTitle = useWatch({
    control,
    name: 'lessonTitle',
  });

  const handleCreateLesson = () => {
    if (!lessonTitle) return;
    const trimmedLessonTitle = lessonTitle.trim();
    createManualLesson(trimmedLessonTitle, editorType);
  };

  return (
    <StyledForm onSubmit={handleSubmit(handleCreateLesson)}>
      <LessonModalMainWrapper>
        <LessonModalContent>
          <TextField
            size="medium"
            type="text"
            label={t('lessonTitle')}
            {...register('lessonTitle', {
              required: { value: true, message: t('errors.lessonTitleFieldIsRequired') },
              setValueAs: (value) => (isString(value) && isEmpty(value.trim()) ? undefined : value),
              onChange: (e) => setValue('lessonTitle', e.target.value),
            })}
            maxLength={300}
            {...(errors['lessonTitle']?.message
              ? { validation: { type: 'negative', message: errors['lessonTitle'].message } }
              : {})}
          />
        </LessonModalContent>
        <Footer
          backButton={{ onClick: back, label: t('back') }}
          nextButton={{
            onClick: () => handleSubmit(handleCreateLesson)(),
            disabled: !lessonTitle,
            label: t('createLesson'),
            iconEnd: <IconLesson />,
          }}
        />
      </LessonModalMainWrapper>
    </StyledForm>
  );
};

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`;
